"use client";

import React from "react";

export interface CapabilitySection {
    tag: string;
    title: string;
    description: string;
    features: string[];
    terminalTitle: string;
    terminalLines: string[];
}

export interface MemorylayerCapabilitiesBlockProps {
    tag?: string;
    sections?: CapabilitySection[];
}

export default function MemorylayerCapabilitiesBlock({
    tag = "System Capabilities",
    sections = [
        {
            tag: "Feature 01",
            title: "Dynamic Vector Memory Stack",
            description: "We built the vector storage layer using pgvector inside PostgreSQL. User dialogue triggers real-time embeddings that match against past conversations, returning persistent contextual memories.",
            features: [
                "PostgreSQL pgvector support for 1536-dim vectors",
                "Cosine similarity indexing for fast query matching (<15ms)",
                "Metadata indexing to separate memories by user account"
            ],
            terminalTitle: "vector_memory_retrieval.sql",
            terminalLines: [
                "SELECT conversation_id, content, 1 - (embedding <=> $1) as similarity",
                "FROM memory_entries",
                "WHERE user_id = $2 AND similarity > 0.82",
                "ORDER BY similarity DESC",
                "LIMIT 5;",
                "",
                "-- Query returned 3 records [Similarity thresholds matched]",
                "-- Retrieval Latency: 12.4ms"
            ]
        },
        {
            tag: "Feature 02",
            title: "Native Polyglot Adaptor",
            description: "The execution engine natively translates instructions and answers across 40+ dialects. We implemented accent settings and vocabulary modifiers, allowing the agent's tone of voice to remain stable during translations.",
            features: [
                "Natively support French, German, Spanish, Mandarin, Japanese",
                "Fine-tuned vocabulary tone retention layer",
                "Optimized multi-language prompt compiler templates"
            ],
            terminalTitle: "polyglot_config.json",
            terminalLines: [
                "{",
                "  \"target_dialects\": [\"es-ES\", \"fr-FR\", \"ja-JP\"],",
                "  \"tone_modifiers\": {",
                "    \"formality\": 0.85,",
                "    \"persona_retention_index\": 0.98",
                "  },",
                "  \"active_compiler_preset\": \"dialect_adaptor_v2\"",
                "}"
            ]
        }
    ]
}: MemorylayerCapabilitiesBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0F0F12] border-t border-b border-zinc-900 w-full" data-header-theme="light">
            <div className="container mx-auto space-y-32">
                
                {/* Header tag */}
                {tag && (
                    <div className="max-w-2xl">
                        <span className="text-xs font-mono text-zinc-500 uppercase tracking-widest block">// {tag}</span>
                    </div>
                )}

                {sections.map((section, idx) => {
                    const isEven = idx % 2 === 0;
                    return (
                        <div key={idx} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                            
                            {/* Left Text / Right Text depending on index */}
                            <div className={`lg:col-span-6 space-y-6 ${!isEven ? "lg:order-2" : ""}`}>
                                <span className="text-xs font-mono text-purple-400 uppercase tracking-widest block">// {section.tag}</span>
                                <h3 className="text-3xl md:text-4xl font-light text-white leading-tight font-sans">
                                    {section.title}
                                </h3>
                                <p className="text-zinc-400 text-sm leading-relaxed font-light font-sans">
                                    {section.description}
                                </p>

                                <ul className="space-y-3 pt-2">
                                    {section.features.map((feat, fIdx) => (
                                        <li key={fIdx} className="flex items-start gap-3 text-xs text-zinc-300 font-sans font-light">
                                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 flex-shrink-0" />
                                            <span>{feat}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Terminal Side Container */}
                            <div className={`lg:col-span-6 ${!isEven ? "lg:order-1" : ""}`}>
                                <div className="bg-[#070709] border border-zinc-850 rounded-xl overflow-hidden shadow-2xl">
                                    {/* Terminal Header */}
                                    <div className="px-4 py-2.5 bg-zinc-900/40 border-b border-zinc-900 flex justify-between items-center text-[10px] font-mono text-zinc-500">
                                        <span>{section.terminalTitle}</span>
                                        <span className="w-2 h-2 rounded-full bg-purple-500/80 animate-pulse" />
                                    </div>
                                    {/* Terminal Code lines */}
                                    <div className="p-5 font-mono text-[10px] md:text-[11px] text-zinc-400 leading-relaxed overflow-x-auto">
                                        {section.terminalLines.map((line, lIdx) => (
                                            <div key={lIdx} className={line.startsWith("--") ? "text-zinc-600" : "text-zinc-400"}>
                                                {line}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                        </div>
                    );
                })}

            </div>
        </section>
    );
}
